<!-- 講座画面、プラン一覧選択時のページ -->
<script setup lang="ts">
  import draggable from "vuedraggable";
  import { PlanInfo } from "~/entities/PlanInfo";
  import { usePlanPriceStore } from "~/stores/PlanPriceStore";
  import { DEFAULT_PAGE_SIZE } from "~/consts/contentsConfig";
  import { type IPlanPriceHistory } from "~/entities/PlanPriceHistory";
  import { useSnackBarStore } from "~/stores/SnackBarStore";
  import { useHistoryStore } from "~/stores/HistoryStore";
  const historyStore = useHistoryStore();

  interface Props {
    planInfo: PlanInfo;
  }
  const localProps = withDefaults(defineProps<Props>(), {});

  defineEmits(["onBack"]);

  const page = ref(1);
  const PAGE_SIZE = 1000; // 並び替えがあるので1000件ほど取得する
  const showDetail = ref(false);

  const snackBarStore = useSnackBarStore();
  const planPriceStore = usePlanPriceStore();

  const loading = computed<boolean>(() => planPriceStore.loading);
  const isDeleteDialogOpened = ref(false);
  const planPrices = ref<IPlanPriceHistory[]>(planPriceStore.planPrices?.plans ?? []);
  const deletedPlanPrice = ref<IPlanPriceHistory | null>(null);

  const computedPage = computed<number>({
    get: () => page.value,
    set: value => {
      page.value = value;
      fetch();
    },
  });

  const showPagination = computed<boolean>(() => {
    if (planPriceStore.planPrices?.totalPages) {
      return planPriceStore.planPrices.totalPages > 1;
    }
    return false;
  });

  // レスポンシブ対応
  const isTablet = isTabletAndSpDisplayRef();

  // 講座ID
  const planId = computed<string>(() => {
    return String(useRoute().query.planId || localProps.planInfo.id);
  });

  // トランジションエフェクトの関係でデータ取得は外部から行う
  const fetch = async () => {
    await planPriceStore.fetch(planId.value, page.value, PAGE_SIZE);
    planPrices.value = planPriceStore.planPrices?.plans ?? [];
    // ページが変わったらスクロール位置をトップに戻す
    window.scrollTo({
      top: 0,
      behavior: "instant",
    });
  };

  const sortedItemsModel = computed<IPlanPriceHistory[]>({
    get: () => {
      return planPrices.value;
    },
    set: value => {
      // 先行して表示に反映
      planPrices.value = value;
      // 並び替え後のデータを保存
      onEndDrag();
    },
  });

  const onEndDrag = async () => {
    try {
      await planPriceStore.setDisplayOrders(planPrices.value);
    } catch (error: any) {
      console.log("プラン並び替え失敗 : " + error.message);
    }
  };

  const onCreate = () => {
    historyStore.to(`/plan/${planId.value}/price/create`);
  };

  const onChange = (planPrice: IPlanPriceHistory) => {
    historyStore.to(`/plan/${planId.value}/price/${planPrice.planPriceId}/edit`);
  };

  const onHistory = (planPrice: IPlanPriceHistory) => {
    historyStore.to(`/plan/${planId.value}/price/${planPrice.planPriceId}/history`);
  };

  const onDelete = (planPrice: IPlanPriceHistory) => {
    deletedPlanPrice.value = planPrice;
    isDeleteDialogOpened.value = true;
  };

  const onCopyUrl = (planPrice: IPlanPriceHistory) => {
    const url = `${window.location.origin}/plan/${planId.value}/join/agreement?planPriceId=${planPrice.planPriceId}`;
    navigator.clipboard.writeText(url);
    snackBarStore.set("クリップボードにコピーしました");
  };

  const deletePrice = async () => {
    if (!deletedPlanPrice.value) {
      snackBarStore.setError("削除に失敗しました。時間をおいて再度お試しください。");
      return;
    }
    try {
      await planPriceStore.deletePrice(deletedPlanPrice.value.planPriceId);
      await fetch();
      snackBarStore.set("プランを削除しました。");
    } catch (error: any) {
      snackBarStore.setError("削除に失敗しました。時間をおいて再度お試しください。");
      sentryErrorLog("プラン削除失敗 : " + error.message);
    }
  };

  // 確認ダイアログ用
  const onClickDeleteYes = () => {
    isDeleteDialogOpened.value = false;
    deletePrice();
  };
  const onClickDeleteNo = () => {
    isDeleteDialogOpened.value = false;
  };

  defineExpose({
    fetch,
    loading,
  });
</script>
<template>
  <MoleculesContainerMain
    v-if="planInfo"
    :default-plan-id="planInfo.id"
    :show-search="false"
    :show-plans="true"
    :enable-all-plan="false"
    :loading="loading"
    title="プラン設定"
    svg-icon="assets/images/icon/member-price.webp"
    @on-back="$emit('onBack')"
  >
    <template #header-btns>
      <v-btn class="add-price" @click="onCreate">
        <v-icon icon="$mdiPlus" color="white" size="large" />
        <span v-if="isTablet" class="text-white">作成 </span>
        <span v-else class="text-white"> プランを作成 </span>
      </v-btn>
    </template>
    <div class="mx-2 mt-2">
      <div v-if="!loading && isTablet" class="d-flex flex-wrap justify-end">
        <v-btn variant="tonal" @click="showDetail = !showDetail">
          <v-icon :icon="showDetail ? '$mdiChevronUp' : '$mdiChevronDown'" />
        </v-btn>
      </div>

      <template v-if="loading">
        <div>
          <!-- ヘッダーはSPでは非表示とする -->
          <MoleculesPlanManagePriceListCard v-if="!isTablet" :header="true" :show-detail="showDetail" />
          <MoleculesPlanManagePriceListCard
            v-for="index in DEFAULT_PAGE_SIZE"
            :key="index"
            :loading="true"
            :show-detail="showDetail"
          />
        </div>
      </template>
      <template v-else>
        <div>
          <!-- ヘッダーはSPでは非表示とする -->
          <MoleculesPlanManagePriceListCard v-if="!isTablet" :header="true" :show-detail="showDetail" />

          <draggable v-model="sortedItemsModel" item-key="planPriceId" handle=".handle">
            <template #item="{ element: item }">
              <MoleculesPlanManagePriceListCard
                :plan-price="item"
                :show-detail="showDetail"
                @on-change="onChange"
                @on-history="onHistory"
                @on-delete="onDelete"
                @on-copy-url="onCopyUrl"
              />
            </template>
          </draggable>
        </div>
      </template>

      <v-sheet color="transparent" class="pb-4">
        <AtomsPagination
          v-if="showPagination"
          v-model:page="computedPage"
          :total-pages="planPriceStore.planPrices?.totalPages || 0"
        />
      </v-sheet>
    </div>
  </MoleculesContainerMain>

  <MoleculesYesNoDialog
    v-model:is-open="isDeleteDialogOpened"
    :message="`${deletedPlanPrice?.planPriceName}を削除します。よろしいですか？`"
    yes-color="main-04"
    @click-yes="onClickDeleteYes"
    @click-no="onClickDeleteNo"
  />

  <AtomsOverlay v-model:loading="loading" />
</template>

<style lang="scss" scoped>
  .add-price-icon {
    margin-top: 2px;
  }
  .add-price {
    background-color: rgba(50, 50, 50, 1);
    font-weight: 700;
  }
</style>
